import React, {
  createContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Device } from "@twilio/voice-sdk";
import { searchRequestData, createRequestData } from "../../Utility/apiHelper";
import {
  requestHideDialogLoader,
  requestHideLoader,
  requestShowDialogLoader,
  requestShowLoader,
} from "../../Store/Global/action";
import { useDispatch } from "react-redux";

export const CallContext = createContext(null);

export const CallProvider = ({ children }) => {
  const dispatch = useDispatch();

  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const inputVolumeRef = useRef(0);
  const mockRingtoneRef = useRef(null);

  const [status, setStatus] = useState("idle");
  const [muted, setMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [activeProvider, setActiveProvider] = useState("");

  const [incomingConnection, setIncomingConnection] = useState(null);
  const [incomingCaller, setIncomingCaller] = useState("");

  // Widget is ALWAYS visible — only "minimized" or "expanded"
  // Starts minimized (permanent fixture in bottom-right)
  const [overlayMode, setOverlayMode] = useState("minimized");
  const [callType, setCallType] = useState(null);
  const [callParams, setCallParams] = useState({});

  // Online/offline listening state
  const [isListening, setIsListening] = useState(false);

  const [pageCallHint, setPageCallHint] = useState(null);
  const [selectedCallerTab, setSelectedCallerTab] = useState("tasks");
  const [selectedCustomerTab, setSelectedCustomerTab] = useState("0");
  const [callerTabFilters, setCallerTabFilters] = useState({
    showOpenLeads: true,
    showOpenTasks: true,
  });
  const [callSession, setCallSession] = useState({
    callSid: "",
    phone: "",
    direction: null,
    lifecycle: "idle",
    canTrack: false,
  });

  const registerPageCallHint = useCallback((hint) => {
    setPageCallHint(hint);
  }, []);

  const clearPageCallHint = useCallback(() => {
    setPageCallHint(null);
  }, []);

  const clearCallSession = useCallback(() => {
    setCallType(null);
    setCallParams({});
    setIncomingCaller("");
    setCallSession({
      callSid: "",
      phone: "",
      direction: null,
      lifecycle: "idle",
      canTrack: false,
    });
    setSelectedCallerTab("tasks");
    setSelectedCustomerTab("0");
    setCallerTabFilters({
      showOpenLeads: true,
      showOpenTasks: true,
    });
  }, []);

  const getCallSid = useCallback((connection) => {
    return (
      connection?.customParameters?.get?.("ParentCallSid") ||
      connection?.parameters?.CallSid ||
      connection?.customParameters?.get?.("CallSid") ||
      connection?.customParameters?.get?.("callSid") ||
      connection?._callSid ||
      ""
    );
  }, []);

  const markCallSessionEnded = useCallback((options = {}) => {
    const { canTrack = false, callSid = "", shouldMinimize = true } = options;
    setCallSession((prev) => ({
      ...prev,
      callSid: callSid || prev.callSid,
      lifecycle: "ended",
      canTrack: canTrack && Boolean(callSid || prev.callSid),
    }));
    if (shouldMinimize) {
      setOverlayMode("minimized");
    }
  }, []);

  const beginOutgoingSession = useCallback((params) => {
    setCallType("outgoing");
    setCallParams(params);
    setIncomingCaller("");
    setIncomingConnection(null);
    setCallSession({
      callSid: params?.CallSid || "",
      phone: params?.phone || "",
      direction: "outgoing",
      lifecycle: "preparing",
      canTrack: false,
    });
    setSelectedCallerTab("tasks");
    setCallerTabFilters({
      showOpenLeads: true,
      showOpenTasks: true,
    });
    setDuration(0);
    setMuted(false);
    inputVolumeRef.current = 0;
  }, []);

  const beginIncomingSession = useCallback((connection, caller) => {
    setCallType("incoming");
    setCallParams({ phone: caller });
    setIncomingCaller(caller);
    setIncomingConnection(connection);
    setCallSession({
      callSid: getCallSid(connection),
      phone: caller,
      direction: "incoming",
      lifecycle: "ringing",
      canTrack: false,
    });
    setSelectedCallerTab("tasks");
    setCallerTabFilters({
      showOpenLeads: true,
      showOpenTasks: true,
    });
    setDuration(0);
    setMuted(false);
    inputVolumeRef.current = 0;
  }, [getCallSid]);

  const timerRef = useRef(null);

  useEffect(() => {
    if (status === "connected") {
      timerRef.current = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [status]);

  const setupDevice = useCallback(
    async (provider) => {
      try {
        dispatch(requestShowDialogLoader());
        setStatus("loading");

        const queryparams = { params: { providerName: provider } };
        const { data } = await searchRequestData(
          queryparams,
          `${process.env.REACT_APP_BASE_URL}api/telecalling/token`
        );

        if (deviceRef.current) {
          deviceRef.current.destroy();
        }

        const device = new Device(data.token, {
          logLevel: 1,
          edge: ["singapore", "mumbai", "roaming"],
          codecPreferences: ["opus", "pcmu"],
        });

        deviceRef.current = device;

        device.on("registered", () => {
          setStatus((prev) => (prev === "loading" ? "ready" : prev));
          createRequestData(
            {},
            `${process.env.REACT_APP_BASE_URL}api/telecalling/presence/online`
          );
        });

        device.on("error", (err) => {
          setStatus("error");
        });

        device.on("incoming", (connection) => {
          setStatus("ringing_incoming");

          const from =
            connection.customParameters?.get("From") ||
            connection.parameters?.From ||
            "Unknown Caller";
          beginIncomingSession(connection, from);
          // setOverlayMode("expanded");



          connection.on("accept", () => {
            setStatus("connected");
            callRef.current = connection;
            setCallSession((prev) => ({
              ...prev,
              callSid: getCallSid(connection) || prev.callSid,
              lifecycle: "active",
              canTrack: false,
            }));
          });
          connection.on("disconnect", () => {
            setStatus(isListening ? "ready" : "idle");
            callRef.current = null;
            setIncomingConnection(null);
            setMuted(false);
            setDuration(0);
            inputVolumeRef.current = 0;
            markCallSessionEnded({
              canTrack: true,
              callSid: getCallSid(connection),
              shouldMinimize: false,
            });
          });
          connection.on("cancel", () => {
            setStatus(isListening ? "ready" : "idle");
            callRef.current = null;
            setIncomingConnection(null);
            setMuted(false);
            setDuration(0);
            inputVolumeRef.current = 0;
            markCallSessionEnded({
              canTrack: false,
              callSid: getCallSid(connection),
              shouldMinimize: false,
            });
          });
          connection.on("reject", () => {
            setStatus(isListening ? "ready" : "idle");
            callRef.current = null;
            setIncomingConnection(null);
            setMuted(false);
            setDuration(0);
            inputVolumeRef.current = 0;
            markCallSessionEnded({
              canTrack: false,
              callSid: getCallSid(connection),
              shouldMinimize: false,
            });
          });
        });

        await device.register();
      } catch (err) {
        setStatus("error");
      } finally {
        dispatch(requestHideDialogLoader());
      }
    },
    [beginIncomingSession, dispatch, getCallSid, isListening, markCallSessionEnded]
  );

  const teardownDevice = useCallback(() => {
    callRef.current?.disconnect();
    deviceRef.current?.destroy();
    deviceRef.current = null;
    callRef.current = null;
    inputVolumeRef.current = 0;
    setStatus("idle");
    setMuted(false);
    setDuration(0);
    setIncomingConnection(null);

    createRequestData(
      {},
      `${process.env.REACT_APP_BASE_URL}api/telecalling/presence/offline`
    );

    if (mockRingtoneRef.current) {
      mockRingtoneRef.current.pause();
      mockRingtoneRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!activeProvider) return;
    setupDevice(activeProvider);
    return () => teardownDevice();
  }, [activeProvider, setupDevice, teardownDevice]);


  const startOutgoingCall = useCallback(
    (params) => {
      const isCallActive =
        status === "calling" ||
        status === "ringing" ||
        status === "connected" ||
        status === "ringing_incoming";

      if (isCallActive) {
        return { blocked: true };
      }

      beginOutgoingSession(params);
      setOverlayMode("expanded");
      return { blocked: false };
    },
    [beginOutgoingSession, status]
  );

  /**
   * Place the actual Twilio call.
   * Called from GlobalCallOverlay when user clicks the Call button.
   */
  const makeCall = useCallback(async (params) => {
    if (!deviceRef.current) return;
    const { phone, ...restParams } = params;
    try {
      beginOutgoingSession(params);
      setDuration(0);
      setStatus("calling");

      const call = await deviceRef.current.connect({
        params: {
          To: phone,
          ...restParams,
        },
      });
      callRef.current = call;
      setCallSession((prev) => ({
        ...prev,
        callSid: getCallSid(call) || prev.callSid,
        lifecycle: "active",
        canTrack: false,
      }));

      call.on("ringing", () => setStatus("ringing"));
      call.on("accept", () => {
        setStatus("connected");
        setCallSession((prev) => ({
          ...prev,
          callSid: getCallSid(call) || prev.callSid,
          lifecycle: "active",
          canTrack: false,
        }));
      });
      call.on("volume", (input) => {
        inputVolumeRef.current = input || 0;
      });
      call.on("disconnect", () => {
        setStatus(isListening ? "ready" : "idle");
        callRef.current = null;
        setMuted(false);
        inputVolumeRef.current = 0;
        setDuration(0);
        markCallSessionEnded({
          canTrack: false,
          callSid: getCallSid(call),
        });
      });
      call.on("cancel", () => {
        setStatus(isListening ? "ready" : "idle");
        callRef.current = null;
        setMuted(false);
        inputVolumeRef.current = 0;
        setDuration(0);
        markCallSessionEnded({
          canTrack: false,
          callSid: getCallSid(call),
        });
      });
      call.on("error", () => {
        setStatus("error");
        callRef.current = null;
        setMuted(false);
        inputVolumeRef.current = 0;
        setDuration(0);
        markCallSessionEnded({
          canTrack: false,
          callSid: getCallSid(call),
        });
      });
    } catch (err) {
      setStatus("error");
      callRef.current = null;
      setMuted(false);
      inputVolumeRef.current = 0;
      setDuration(0);
    }
  }, [beginOutgoingSession, getCallSid, isListening, markCallSessionEnded]);

  /** End the active call — stays in minimized/expanded mode. */
  const endCall = useCallback(() => {
    callRef.current?.disconnect();
    callRef.current = null;
    setStatus(isListening ? "ready" : "idle");
    setMuted(false);
    inputVolumeRef.current = 0;
    setDuration(0);
    setIncomingConnection(null);
    markCallSessionEnded({
      canTrack: callType === "incoming",
      shouldMinimize: callType !== "incoming",
    });
  }, [callType, isListening, markCallSessionEnded]);

  /** Toggle mute. */
  const toggleMute = useCallback(() => {
    const newMuted = !muted;
    setMuted(newMuted);
    callRef.current?.mute(newMuted);
    if (newMuted) inputVolumeRef.current = 0;
  }, [muted]);

  /** Accept an incoming call. */
  const acceptCall = useCallback(() => {
    if (incomingConnection) {
      setDuration(0);
      incomingConnection.accept();
    }
  }, [incomingConnection]);

  /** Reject an incoming call. */
  const rejectCall = useCallback(() => {
    if (incomingConnection) {
      incomingConnection.reject();
    }
  }, [incomingConnection]);

  /** Go Online — register Twilio device for receiving calls. */
  const startListening = useCallback((provider) => {
    localStorage.setItem("tc_auto_online_provider", provider);
    setActiveProvider(provider);
    setIsListening(true);
  }, []);

  /** Go Offline — destroy device. */
  const stopListening = useCallback(() => {
    localStorage.removeItem("tc_auto_online_provider");
    setIsListening(false);
    setActiveProvider("");
    teardownDevice();
  }, [teardownDevice]);

  /** Minimize the widget. */
  const minimize = useCallback(() => {
    setOverlayMode("minimized");
  }, []);

  /** Expand the widget. */
  const expand = useCallback(() => {
    setOverlayMode("expanded");
  }, []);

  /**
   * "End and dismiss" the active call — goes back to minimized idle state.
   * Widget stays visible (no hidden mode).
   */
  const dismissCall = useCallback(() => {
    if (
      status === "calling" ||
      status === "ringing" ||
      status === "connected"
    ) {
      callRef.current?.disconnect();
      callRef.current = null;
      inputVolumeRef.current = 0;
    }
    setStatus(isListening ? "ready" : "idle");
    clearCallSession();
    setDuration(0);
    setMuted(false);
    setIncomingConnection(null);
  }, [clearCallSession, status, isListening]);

  const completeTrackedCallSession = useCallback(() => {
    setStatus(isListening ? "ready" : "idle");
    clearCallSession();
    setDuration(0);
    setMuted(false);
    setIncomingConnection(null);
    inputVolumeRef.current = 0;
    setOverlayMode("minimized");
  }, [clearCallSession, isListening]);

  // useEffect(() => {
  //   window.mockIncomingCall = (callerName = "+918882092484") => {
  //     if (mockRingtoneRef.current) {
  //       mockRingtoneRef.current.pause();
  //       mockRingtoneRef.current = null;
  //     }

  //     setStatus("ringing_incoming");
  //     setCallType("incoming");
  //     setIncomingCaller(callerName);

  //     const audio = new Audio("https://assets.mixkit.co/active_storage/sfx/1359/1359-preview.mp3");
  //     audio.loop = true;
  //     audio.play().catch(e => console.warn("Autoplay blocked by browser. Interaction required.", e));
  //     mockRingtoneRef.current = audio;

  //     // Notify backend for CallLog recording
  //     const provider = "Twilio";
  //     const callSid = `mock_${Math.random().toString(36).substring(7)}`;
  //     setCallSession({
  //       callSid,
  //       phone: callerName,
  //       direction: "incoming",
  //       lifecycle: "ringing",
  //       canTrack: false,
  //     });

  //     const params = new URLSearchParams();
  //     params.append("CallSid", callSid);
  //     params.append("From", callerName);
  //     params.append("To", "+910000000000"); // Mock target
  //     params.append("Direction", "inbound");
  //     params.append("CallStatus", "ringing");

  //     fetch(`${process.env.REACT_APP_BASE_URL}api/telecalling/incoming/${provider}/3?${params.toString()}`, {
  //       method: "POST",
  //     }).catch(err => console.error("Mock Call: fetch failed", err));
      
  //     const mockConnection = {
  //       accept: () => {
  //         if (mockRingtoneRef.current) {
  //           mockRingtoneRef.current.pause();
  //           mockRingtoneRef.current = null;
  //         }
  //         setStatus("connected");
  //         setCallSession((prev) => ({
  //           ...prev,
  //           lifecycle: "active",
  //           canTrack: false,
  //         }));
  //         callRef.current = {
  //           disconnect: () => {
  //             setStatus(isListening ? "ready" : "idle");
  //             setDuration(0);
  //             // Do not clear context so post-call view stays
  //             // setCallType(null);
  //             setIncomingConnection(null);
  //             // setIncomingCaller("");
  //             markCallSessionEnded({ canTrack: true, callSid });
  //           },
  //           mute: () => {}
  //         };
  //         setIncomingConnection(null); 
  //       },
  //       reject: () => {
  //         if (mockRingtoneRef.current) {
  //           mockRingtoneRef.current.pause();
  //           mockRingtoneRef.current = null;
  //         }
  //         setStatus(isListening ? "ready" : "idle");
  //         // Do not clear context on explicit reject
  //         // setCallType(null);
  //         setIncomingConnection(null);
  //         // setIncomingCaller("");
  //         markCallSessionEnded({ canTrack: false, callSid });
  //       }
  //     };
      
  //     setIncomingConnection(mockConnection);
  //   };

  //   return () => {
  //     delete window.mockIncomingCall;
  //     if (mockRingtoneRef.current) {
  //       mockRingtoneRef.current.pause();
  //       mockRingtoneRef.current = null;
  //     }
  //   };
  // }, [isListening, activeProvider, markCallSessionEnded]);

  const pageCallHintRef = useRef(pageCallHint);
  useEffect(() => {
    pageCallHintRef.current = pageCallHint;
  }, [pageCallHint]);

  useEffect(() => {
    if (callSession?.lifecycle === "ended") {
      const isOutgoing = callSession?.direction === "outgoing";
      if (!isOutgoing) return;
      dispatch(requestShowLoader());

      const timer = setTimeout(() => {
        dispatch(requestHideLoader());
        if (typeof pageCallHintRef.current?.onCallActivityReady === "function") {
          pageCallHintRef.current.onCallActivityReady();
        }
        completeTrackedCallSession();
      }, 2000);

      return () => {
        clearTimeout(timer);
        dispatch(requestHideLoader());
      };
    }
  }, [callSession?.lifecycle, callSession?.direction, dispatch, completeTrackedCallSession]);

  useEffect(() => {
    const savedProvider = localStorage.getItem("tc_auto_online_provider");
    if (savedProvider) {
      startListening(savedProvider);
    }
  }, [startListening]);

  const contextValue = useMemo(
    () => ({
      status,
      muted,
      duration,
      overlayMode,
      callType,
      callParams,
      isListening,
      incomingCaller,
      activeProvider,
      selectedCallerTab,
      selectedCustomerTab,
      callerTabFilters,
      callSession,
      inputVolumeRef,

      startOutgoingCall,
      makeCall,
      setActiveProvider,

      startListening,
      stopListening,
      acceptCall,
      rejectCall,

      endCall,
      toggleMute,

      minimize,
      expand,
      dismissCall,
      completeTrackedCallSession,
      setOverlayMode,
      setCallType,
      setCallParams,
      setSelectedCallerTab,
      setSelectedCustomerTab,
      setCallerTabFilters,

      pageCallHint,
      registerPageCallHint,
      clearPageCallHint,
    }),
    [
      status,
      muted,
      duration,
      overlayMode,
      callType,
      callParams,
      isListening,
      incomingCaller,
      activeProvider,
      selectedCallerTab,
      selectedCustomerTab,
      callerTabFilters,
      callSession,
      startOutgoingCall,
      makeCall,
      startListening,
      stopListening,
      acceptCall,
      rejectCall,
      endCall,
      toggleMute,
      minimize,
      expand,
      dismissCall,
      completeTrackedCallSession,
      setCallParams,
      setCallType,
      setSelectedCallerTab,
      setSelectedCustomerTab,
      setCallerTabFilters,
      pageCallHint,
      registerPageCallHint,
      clearPageCallHint,
    ]
  );

  return (
    <CallContext.Provider value={contextValue}>{children}</CallContext.Provider>
  );
};
